<template>
  <b-card-code
    title="Modifier un poids volumétrique"
    aria-label="Formulaire de modification d'un poids volumétrique"
  >
    <b-card-text>
      <span>Veuillez modifier les informations du poids volumétrique utilisé pour la logistique des colis. Tous les champs obligatoires doivent être renseignés.</span>
    </b-card-text>

    <!-- form -->
    <validation-observer ref="simpleRules">
      <b-form
        v-if="weight"
        aria-label="Formulaire de modification de poids volumétrique"
      >
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nom du poids volumétrique"
              label-for="weight"
              aria-label="Nom du poids volumétrique"
              description="Saisissez ou modifiez le nom distinctif de ce poids volumétrique."
            >
              <validation-provider
                #default="{ errors }"
                name="poids"
                rules="required"
              >
                <b-form-input
                  id="weight"
                  v-model="weight.weight"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Nom du poids volumétrique"
                  aria-label="Entrée du nom du poids volumétrique"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Unité de mesure"
              label-for="unit"
              aria-label="Unité de mesure du poids volumétrique"
              description="Indiquez l'unité de mesure (ex : kg, g, etc.)."
            >
              <b-form-input
                id="unit"
                v-model="weight.unit"
                placeholder="kg, g, etc."
                aria-label="Entrée de l'unité de mesure"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Poids minimum"
              label-for="min"
              aria-label="Poids minimum autorisé"
              description="Saisissez ou modifiez le poids minimum accepté pour cette catégorie."
            >
              <b-form-input
                id="min"
                v-model="weight.min"
                type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                placeholder="Poids minimum"
                aria-label="Entrée du poids minimum"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Poids maximum"
              label-for="max"
              aria-label="Poids maximum autorisé"
              description="Saisissez ou modifiez le poids maximum accepté pour cette catégorie."
            >
              <b-form-input
                id="max"
                v-model="weight.max"
                type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                placeholder="Poids maximum"
                aria-label="Entrée du poids maximum"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group
              label="Description"
              label-for="description"
              aria-label="Description du poids volumétrique"
              description="Ajoutez ou modifiez une description pour préciser l'usage ou les restrictions de ce poids."
            >
              <b-form-textarea
                id="description"
                v-model="weight.description"
                rows="3"
                placeholder="Description du poids volumétrique"
                aria-label="Entrée de la description du poids volumétrique"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group aria-label="Statut du poids volumétrique">
              <b-form-checkbox
                id="is_active"
                v-model="weight.is_active"
                name="is_active"
                switch
                size="lg"
                aria-label="Basculer le statut du poids volumétrique"
              >
                {{ weight.is_active ? 'Actif' : 'Inactif' }}
              </b-form-checkbox>
              <span class="sr-only">Activez ou désactivez ce poids volumétrique pour le rendre disponible ou non dans le système logistique.</span>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-button
              variant="primary"
              type="submit"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :class="{ 'disabled-cursor': isLoading || !isWeightValid }"
              :disabled="isLoading || !isWeightValid"
              aria-label="Enregistrer les modifications du poids volumétrique"
              @click.prevent="editWeight"
            >
              Modifier
              <span class="sr-only">Valider la modification du poids volumétrique</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card-code>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BCardCode from '@core/components/b-card-code'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import {
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BCardText,
} from 'bootstrap-vue'
import axios from 'axios'

// Add this at the top of your script section to register the required validator
extend('required', {
  validate(value) {
    return {
      required: true,
      valid: ['', null, undefined].indexOf(value) === -1,
    }
  },
  message: 'Ce champ est obligatoire',
  computesRequired: true,
})

export default {
  components: {
    BCardCode,
    ValidationProvider,
    ValidationObserver,
    BCardText,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  data() {
    return {
      id: '',
      weight: {},
      isLoading: false,
    }
  },
  computed: {
    isWeightValid() {
      return this.weight.weight !== ''
    },
  },
  created() {
    this.getWeight()
    this.id = this.$route.params.id
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async getWeight() {
      try {
        this.isLoading = true
        const { data } = await axios.get(`/api/weights/detail/${this.$route.params.id}/`)
        this.weight = {
          ...data,
          // Ensure fields have a default value if they're null
          min: data.min || null,
          max: data.max || null,
          unit: data.unit || 'kg',
          description: data.description || '',
          is_active: typeof data.is_active === 'boolean' ? data.is_active : true,
        }
        this.isLoading = false
      } catch (error) {
        setTimeout(() => {
          this.showToast('danger', 'top-center', error.toString())
        }, 1000)
      }
    },
    async editWeight() {
      const { simpleRules } = this.$refs
      const isValid = await simpleRules.validate()
      if (isValid) {
        try {
          this.isLoading = true

          // Format numeric fields
          const payload = {
            ...this.weight,
            min: this.weight.min ? parseFloat(this.weight.min) : null,
            max: this.weight.max ? parseFloat(this.weight.max) : null,
          }

          await axios.put(`/api/weights/update/${this.id}/`, payload)

          setTimeout(() => {
            this.showToast('success', 'top-center', 'poids modifié avec succés')
          }, 1000)

          // Fix: Use named route instead of raw path
          this.$router.push({ name: 'weight-list' })
          this.isLoading = false
        } catch (error) {
          setTimeout(() => {
            this.showToast('danger', 'top-center', error.toString())
          }, 1000)
        }
      } else {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'champ obligatoire ! vérifier votre donnée !')
        }, 1000)
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

// Consistent form field sizing
.form-control, .vs__dropdown-toggle {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #ea5455;
    box-shadow: none;
  }
}

// Textarea exception for height
textarea.form-control {
  height: auto;
}

// Improve v-select styling to match regular inputs
.vs__dropdown-toggle {
  padding: 5px 0;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.vs__selected-options {
  padding: 0 6px;
  align-items: center;
}

.vs__search {
  padding: 5px 8px;
  margin: 0;
}

.vs__actions {
  padding-top: 0;
  padding-bottom: 0;
}

.vs__clear {
  height: 22px;
}

.vs__open-indicator {
  height: 17px;
}
</style>
